import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Reusable form field: label + input + optional help text + error display.
 * Wraps shadcn <Input /> and <Label /> with the PagePledge error styling.
 *
 * Props:
 *   id, name, label, type, value, onChange, placeholder, required, autoComplete, autoFocus
 *   error: string | string[] — validation error(s) from the server
 *   help:  optional hint, shown between the label and the input so it's read
 *          before typing (errors go below the input)
 *   note:  optional feedback on the current value (progress, what the
 *          chosen option means), shown below the input
 *   children: optional custom input (overrides the default <Input />)
 */
export default function FormField({
  id,
  name,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  autoComplete,
  autoFocus = false,
  error,
  help,
  note,
  children,
  ...rest
}) {
  const errorText = Array.isArray(error) ? error[0] : error;
  const fieldId = id || name;
  const describedBy = [help && `${fieldId}-help`, errorText ? `${fieldId}-error` : note && `${fieldId}-note`].filter(Boolean).join(" ") || undefined;

  return (
    <div className="space-y-2">
      <FieldLabel fieldId={fieldId} label={label} required={required} help={help} />
      {children || (
        <Input
          id={fieldId}
          name={name}
          type={type}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-invalid={!!errorText}
          aria-describedby={describedBy}
          className={errorText ? "border-destructive focus-visible:ring-destructive" : ""}
          {...rest}
        />
      )}
      {errorText && (
        <p id={`${fieldId}-error`} className="text-sm text-destructive">
          {errorText}
        </p>
      )}
      {!errorText && note && (
        <p id={`${fieldId}-note`} className="text-sm text-muted-foreground">
          {note}
        </p>
      )}
    </div>
  );
}

// The label with its hint underneath, shared by FormField and SelectField.
export function FieldLabel({ fieldId, label, required, help }) {
  if (!label && !help) return null;
  return (
    <div className="space-y-1">
      {label && (
        <Label htmlFor={fieldId}>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
      )}
      {help && (
        <p id={`${fieldId}-help`} className="text-sm text-muted-foreground">
          {help}
        </p>
      )}
    </div>
  );
}
